import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Button from '../../components/UI/Button';

type FormData = {
  amount: string;
  customAmount: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  recurrence: 'once' | 'monthly';
};

const DonationForm: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState('');
  
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      amount: '',
      recurrence: 'once',
    }
  });

  const amount = watch('amount');
  const customAmount = watch('customAmount');

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1000));
    
    const finalAmount = data.amount === 'custom' ? data.customAmount : data.amount;
    
    reset();
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    // Reset success message after 5 seconds
    setTimeout(() => {
      setIsSubmitted(false);
    }, 5000);
  };

  const handleAmountClick = (value: string) => {
    setSelectedAmount(value);
  };

  const predefinedAmounts = ['50', '100', '200', '500'];

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isSubmitted && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          <p>Mulțumim pentru donația ta! Vei primi un email de confirmare în curând.</p>
        </div>
      )}
      
      <div className="mb-6">
        <label className="block text-gray-700 font-medium mb-2">Alege suma *</label>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {predefinedAmounts.map((amt) => (
            <label
              key={amt}
              className={`p-3 border rounded-md text-center cursor-pointer transition-colors ${
                amount === amt
                  ? 'border-accent bg-accent-100 text-accent'
                  : 'border-gray-300 hover:border-accent-light'
              }`}
              onClick={() => handleAmountClick(amt)}
            >
              <input
                type="radio"
                value={amt}
                className="sr-only"
                {...register('amount', { required: 'Selectează o sumă' })}
              />
              {amt} RON
            </label>
          ))}
        </div>
        <label
          className={`p-3 border rounded-md flex items-center cursor-pointer transition-colors ${
            amount === 'custom'
              ? 'border-accent bg-accent-100'
              : 'border-gray-300 hover:border-accent-light'
          }`}
          onClick={() => handleAmountClick('custom')}
        >
          <input
            type="radio"
            value="custom"
            className="sr-only"
            {...register('amount', { required: 'Selectează o sumă' })}
          />
          <span className="mr-2">Altă sumă:</span>
          <input
            type="number"
            placeholder="RON"
            min="1"
            className={`w-full p-1 border rounded focus:outline-none focus:ring-1 focus:ring-accent ${
              amount === 'custom' ? 'border-accent' : 'border-gray-300'
            }`}
            onClick={(e) => e.stopPropagation()}
            {...register('customAmount', {
              required: amount === 'custom' ? 'Introdu o sumă' : false,
            })}
          />
        </label>
        {errors.amount && (
          <p className="text-red-500 text-sm mt-1">{errors.amount.message}</p>
        )}
        {amount === 'custom' && errors.customAmount && (
          <p className="text-red-500 text-sm mt-1">{errors.customAmount.message}</p>
        )}
      </div>
      
      <div className="mb-6">
        <label className="block text-gray-700 font-medium mb-2">Recurență</label>
        <div className="grid grid-cols-2 gap-2">
          <label
            className={`p-3 border rounded-md text-center cursor-pointer transition-colors ${
              watch('recurrence') === 'once'
                ? 'border-accent bg-accent-100 text-accent'
                : 'border-gray-300 hover:border-accent-light'
            }`}
          >
            <input
              type="radio"
              value="once"
              className="sr-only"
              {...register('recurrence')}
            />
            O singură dată
          </label>
          <label
            className={`p-3 border rounded-md text-center cursor-pointer transition-colors ${
              watch('recurrence') === 'monthly'
                ? 'border-accent bg-accent-100 text-accent'
                : 'border-gray-300 hover:border-accent-light'
            }`}
          >
            <input
              type="radio"
              value="monthly"
              className="sr-only"
              {...register('recurrence')}
            />
            Lunar
          </label>
        </div>
      </div>
      
      <div className="mb-4">
        <label htmlFor="firstName" className="block text-gray-700 font-medium mb-1">Prenume *</label>
        <input
          id="firstName"
          type="text"
          className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-accent/50 ${
            errors.firstName ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Prenumele tău"
          {...register('firstName', { required: 'Prenumele este obligatoriu' })}
        />
        {errors.firstName && (
          <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>
        )}
      </div>
      
      <div className="mb-4">
        <label htmlFor="lastName" className="block text-gray-700 font-medium mb-1">Nume *</label>
        <input
          id="lastName"
          type="text"
          className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-accent/50 ${
            errors.lastName ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Numele tău"
          {...register('lastName', { required: 'Numele este obligatoriu' })}
        />
        {errors.lastName && (
          <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>
        )}
      </div>
      
      <div className="mb-4">
        <label htmlFor="email" className="block text-gray-700 font-medium mb-1">Email *</label>
        <input
          id="email"
          type="email"
          className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-accent/50 ${
            errors.email ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Email-ul tău"
          {...register('email', { 
            required: 'Email-ul este obligatoriu',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Adresa de email este invalidă'
            }
          })}
        />
        {errors.email && (
          <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
        )}
      </div>
      
      <div className="mb-6">
        <label htmlFor="phone" className="block text-gray-700 font-medium mb-1">Telefon</label>
        <input
          id="phone"
          type="tel"
          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent/50"
          placeholder="Număr de telefon (opțional)"
          {...register('phone')}
        />
      </div>
      
      <Button
        type="submit"
        variant="accent"
        fullWidth
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Se procesează...' : 'Donează acum'}
      </Button>
      <p className="text-sm text-gray-500 mt-2 text-center">
        * Câmpuri obligatorii
      </p>
    </form>
  );
};

export default DonationForm;